import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';

interface Room {
  name: string;
  dimensions: string;
  area: string;
}

interface FloorPlanData {
  id: string;
  title: string;
  totalArea: string;
  rooms: string;
  image: string;
  description: string;
  roomDetails: Room[];
  features: string[];
}

interface FloorPlanDetailProps {
  data: FloorPlanData;
}

export default function FloorPlanDetail({ data }: FloorPlanDetailProps) {
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      {/* Breadcrumb */}
      <div className="bg-gray-50 py-6 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4">
          <nav className="flex items-center gap-2 text-sm text-gray-600">
            <Link to="/" className="hover:text-teal-600 transition-colors">Ana Sayfa</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/floor-plans" className="hover:text-teal-600 transition-colors">Kat Planları</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-gray-900 font-medium">{data.title}</span>
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 bg-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          {/* Header */}
          <div className="mb-12">
            <h1 className="text-5xl font-bold text-gray-900 mb-4">{data.title}</h1>
            <div className="flex flex-wrap items-center gap-6 text-gray-600">
              <div className="flex items-center gap-2">
                <i className="ri-layout-line text-teal-600 text-xl"></i>
                <span className="font-medium">{data.rooms}</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-ruler-line text-teal-600 text-xl"></i>
                <span className="font-medium">{data.totalArea}</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Floor Plan Image */}
            <div className="lg:col-span-2">
              <div 
                className="relative bg-gray-100 rounded-2xl overflow-hidden shadow-xl cursor-pointer group"
                onClick={() => setIsLightboxOpen(true)}
              >
                <img
                  src={data.image}
                  alt={data.title}
                  className="w-full h-auto"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
                  <div className="w-16 h-16 flex items-center justify-center bg-white rounded-full opacity-0 group-hover:opacity-100 transform scale-75 group-hover:scale-100 transition-all duration-300">
                    <i className="ri-zoom-in-line text-2xl text-teal-600"></i>
                  </div>
                </div>
              </div>
              
              <p className="text-gray-600 mt-6 leading-relaxed">
                {data.description}
              </p>

              {/* Room Details Table */}
              <div className="mt-10">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Oda Detayları</h3>
                <div className="bg-gray-50 rounded-xl overflow-hidden border border-gray-200">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-teal-600 text-white">
                        <th className="px-6 py-4 text-left font-semibold text-sm">Oda</th>
                        <th className="px-6 py-4 text-left font-semibold text-sm">Boyutlar</th>
                        <th className="px-6 py-4 text-left font-semibold text-sm">Alan</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.roomDetails.map((room, index) => (
                        <tr 
                          key={index}
                          className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} border-b border-gray-200 last:border-b-0`}
                        >
                          <td className="px-6 py-4 text-gray-900 font-medium text-sm">{room.name}</td>
                          <td className="px-6 py-4 text-gray-600 text-sm">{room.dimensions}</td>
                          <td className="px-6 py-4 text-gray-900 font-semibold text-sm">{room.area}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                {/* Features Card */}
                <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-2xl p-8 shadow-lg">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">Özellikler</h3>
                  <div className="space-y-4">
                    {data.features.map((feature, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <div className="w-6 h-6 flex items-center justify-center bg-teal-600 rounded-full flex-shrink-0 mt-0.5">
                          <i className="ri-check-line text-white text-sm"></i>
                        </div>
                        <span className="text-gray-800 text-sm leading-relaxed">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* CTA Card */}
                <div className="bg-gradient-to-br from-red-600 to-red-700 rounded-2xl p-8 shadow-xl text-white">
                  <div className="w-14 h-14 flex items-center justify-center bg-white/20 rounded-xl mb-4">
                    <i className="ri-phone-line text-3xl"></i>
                  </div>
                  <h3 className="text-2xl font-bold mb-3">Bilgi Almak İster misiniz?</h3>
                  <p className="text-white/90 mb-6 text-sm leading-relaxed">
                    Uzman ekibimiz size yardımcı olmak için hazır. Hemen iletişime geçin.
                  </p>
                  <a 
                    href="tel:+905326771100"
                    className="block w-full bg-white text-red-600 text-center py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap"
                  >
                    Hemen Ara
                  </a>
                  <Link
                    to="/contact"
                    className="block w-full bg-white/10 text-white text-center py-3 rounded-lg font-semibold hover:bg-white/20 transition-colors mt-3 whitespace-nowrap"
                  >
                    İletişim Formu
                  </Link>
                </div>

                {/* Other Plans */}
                <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Diğer Planlar</h3>
                  <div className="space-y-3">
                    <Link 
                      to="/floor-plans/2-1"
                      className={`block p-4 rounded-lg transition-colors ${data.id === '2-1' ? 'bg-teal-50 border-2 border-teal-600' : 'bg-gray-50 hover:bg-gray-100'}`}
                    >
                      <div className="font-semibold text-gray-900 text-sm mb-1">2+1 Örnek Daire</div>
                      <div className="text-xs text-gray-600">95 m²</div>
                    </Link>
                    <Link 
                      to="/floor-plans/3-1"
                      className={`block p-4 rounded-lg transition-colors ${data.id === '3-1' ? 'bg-teal-50 border-2 border-teal-600' : 'bg-gray-50 hover:bg-gray-100'}`}
                    >
                      <div className="font-semibold text-gray-900 text-sm mb-1">3+1 Örnek Daire</div>
                      <div className="text-xs text-gray-600">125 m²</div>
                    </Link>
                    <Link 
                      to="/floor-plans/3-1-terrace"
                      className={`block p-4 rounded-lg transition-colors ${data.id === '3-1-terrace' ? 'bg-teal-50 border-2 border-teal-600' : 'bg-gray-50 hover:bg-gray-100'}`}
                    >
                      <div className="font-semibold text-gray-900 text-sm mb-1">3+1 Teraslı Daire</div>
                      <div className="text-xs text-gray-600">145 m²</div>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox Modal */}
      {isLightboxOpen && (
        <div 
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4"
          onClick={() => setIsLightboxOpen(false)}
        >
          <button
            onClick={() => setIsLightboxOpen(false)}
            className="absolute top-6 right-6 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
          <img
            src={data.image}
            alt={data.title}
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      <Footer />
    </div>
  );
}